/* eslint-disable jsx-a11y/tabindex-no-positive */
import React, { useEffect, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Container, Tab, Tabs, Typography, useTheme } from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import AddIcon from '@mui/icons-material/Add';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { di } from '../../state/inversify.config';
import { ConneqtPlayerDomain } from './conneqtPlayer.domain';
import { LoginDomainService } from '../login/loginDomainService';
import { useQuerySync } from '../../state/queryParams/useQuerySync';
import { ConneqtPageQueryService } from './conneqt.queryParam.service';
import { ConneqtPageQuery } from './conneqt.queryParams.interface';
import { ConneqtSubRoutes } from './conneqtSubRoutes';
import { AddConneqt } from './addConneqt/addConneqt';
import { AlertDialog } from '../../components/alertDialog/alertDialog';
import { Banner } from '../../components/banner/banner';
import { Button } from '../../components/button/button';

interface ConneqtPageProps {
  pageQueryParam: ConneqtPageQueryService;
  showReceived: boolean;
}
export function ConneqtPage({
  pageQueryParam,
  showReceived,
}: ConneqtPageProps) {
  // WARNING: don't remove useQuerySync and don't implement it anywhere else.
  // TODO: find a solutions that ensures these two rules.
  useQuerySync<ConneqtPageQuery>(pageQueryParam);

  const { t } = useTranslation();
  const navigator = useNavigate();
  const theme = useTheme();
  const conneqtPlayerDomain = di.get(ConneqtPlayerDomain);
  const noConneqtExist = conneqtPlayerDomain.useConneqtExistSelector();

  const loginDomainService = di.get(LoginDomainService);
  const isNetworkConnected = loginDomainService.useNetworkSelector();

  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);

  const [value, setValue] = useState(0);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  useEffect(() => {
    const url = window.location.href.split('/').pop();
    if (url?.includes('sent')) {
      setValue(0);
    }
    if (url?.includes('received')) {
      setValue(1);
    }
    if (url?.includes('all')) {
      setValue(2);
    }
  }, []);

  return (
    <Container>
      {!isNetworkConnected && (
        <Box sx={{ my: 1 }}>
          <Banner
            title={t('OFFLINE_MODE')}
            severity="error"
            icon={<WarningIcon />}
          />
        </Box>
      )}

      <Typography
        id="conneqt-title"
        variant="h5"
        component="h5"
        fontWeight="bold"
        sx={{ my: 1 }}
      >
        {t('CONNEQT')}
      </Typography>
      {noConneqtExist && (
        <Banner
          severity="info"
          elevation={2}
          title={t('NO_CONNEQT_REQUEST_EXIST')}
          icon={<InfoOutlinedIcon />}
        />
      )}

      <Box>
        {isNetworkConnected && (
          <Button
            testId="conneqtRequest"
            type="submit"
            variant="contained"
            onClick={handleOpen}
            size="large"
            startIcon={<AddIcon />}
            label={t('CONNEQT_REQUEST')}
            sx={{ my: 2 }}
            color="secondary"
          />
        )}
      </Box>

      <AlertDialog
        title={t('CONNEQT_REQUEST').toString()}
        setOpen={setOpen}
        open={open}
        content={<AddConneqt showAddConnectModal={setOpen} />}
      />

      <Box>
        <Tabs
          value={value}
          onChange={handleChange}
          aria-label={t('accessibility.TABS')}
        >
          <Tab
            value={0}
            label={t('SENT')}
            onClick={() => navigator(ConneqtSubRoutes.sent)}
            sx={{ color: theme.palette.text.primary }}
            tabIndex={0}
          />
          {showReceived && (
            <Tab
              value={1}
              label={t('RECEIVE')}
              data-testid="receive"
              onClick={() => navigator(ConneqtSubRoutes.received)}
              sx={{ color: theme.palette.text.primary }}
              tabIndex={1}
            />
          )}
          <Tab
            value={2}
            data-testid="conneqted"
            label={t('CONNEQT_CONNEQTED')}
            onClick={() => navigator(ConneqtSubRoutes.all)}
            sx={{ color: theme.palette.text.primary }}
            tabIndex={2}
          />
        </Tabs>
      </Box>
      <Outlet />
    </Container>
  );
}
